<template>
    <CModal
        v-if="!showFeedbackModalThanks"
        class="VolunteerWeekProjectRequestModal"
        :hide-closer="state === 'loading'"
        :model-value="true"
        :prevent-backdrop-click="state === 'loading'"
        size="xl"
        title="Záujem o dobrovoľnícku príležitosť"
        @update:model-value="onModalModelUpdate"
    >
        <template v-if="state !== 'done'">
            <div v-if="state === 'error'" class="mb-4 text-red-500">
                {{ errors.general }}
            </div>

            <div v-if="!userProfile || showLoggedInNameFields">
                <FirstName v-model="unregisteredUser.firstName" :error="errors.firstName" :lang="lang" required />

                <LastName v-model="unregisteredUser.lastName" :error="errors.lastName" :lang="lang" required />
            </div>

            <CFormGroup :error="errors.email" label="Email" required>
                <CFormInput
                    v-model="projectRequest.email"
                    autocomplete="section-primary email"
                    name="contactEmail"
                    placeholder="tvoj@email.sk"
                />
            </CFormGroup>

            <CFormGroup label="Názov firmy/školy">
                <CFormInput
                    v-model="projectRequest.companyName"
                    autocomplete="organization"
                    name="companyName"
                    placeholder="Názov firmy/školy"
                />
            </CFormGroup>

            <CFormGroup :error="errors.phone" label="Telefón" required>
                <CFormInput
                    v-model="projectRequest.phone"
                    autocomplete="tel"
                    inputmode="tel"
                    placeholder="+421XXXXXXXXX"
                    type="tel"
                />
            </CFormGroup>

            <CFormGroup :error="errors.volunteerCount" label="Koľko ľudí chceš na aktivitu prihlásiť" required>
                <CFormInput
                    v-model="projectRequest.volunteerCount"
                    :disabled="!hasAvailableSpots"
                    :max="availableSpots"
                    :min="hasAvailableSpots ? 1 : 0"
                    type="number"
                />
                <div class="mt-1 text-sm text-gray-600 dark:text-txt-body">
                    Voľné miesta: {{ availableSpots }} (z {{ project.maxVolunteerCount || 0 }})
                </div>
                <div v-if="!hasAvailableSpots" class="mt-2 text-sm text-red-600 dark:text-red-300">
                    Táto dobrovoľnícka príležitosť je už obsadená.
                </div>
            </CFormGroup>

            <CFormGroup v-if="showBackupContactMail" :error="errors.backupContactMail" label="Druhá emailová adresa">
                <CFormInput
                    v-model="projectRequest.backupContactMail"
                    autocomplete="section-backup email"
                    name="backupContactMail"
                    placeholder="nahradna@firma.sk"
                />
            </CFormGroup>

            <CFormGroup
                :error="errors.motivation"
                label="Napíš správu organizácii, prečo a ako im pomôžeš"
                :label-info="`${getLength(projectRequest.motivation)}/700`"
                required
            >
                <CFormTextarea
                    v-model="projectRequest.motivation"
                    maxlength="700"
                    placeholder="Začni písať správu organizácii …"
                />
            </CFormGroup>

            <CFormGroup :error="errors.consent" required>
                <CFormCheckbox v-model="consent" required>
                    Súhlasím, aby moje hore uvedené údaje / profil, poskytol prevádzkovateľ portálu GROWNi organizácii
                    {{ org.title }} a príslušnému dobrovoľníckemu centru. Viac o
                    <a class="link inline" href="/privacy" target="_blank">spracovaní osobných údajov</a>.
                </CFormCheckbox>
            </CFormGroup>

            <div class="mt-2 flex w-full justify-end">
                <CButton
                    class="w-full sm:w-auto"
                    :disabled="state === 'loading' || !hasAvailableSpots"
                    :loading="state === 'loading'"
                    size="large"
                    variant="primary"
                    @click.prevent="submitApplication()"
                >
                    Odoslať záujem
                </CButton>
            </div>
        </template>
        <template v-else>
            <div class="text-center">
                <h3 class="text-green-600 dark:text-green-300 mb-4 text-xl font-bold">Ďakujeme za tvoj záujem!</h3>
                <p class="mb-4">
                    Tvoja žiadosť o dobrovoľníctvo bola úspešne odoslaná organizácii
                    <strong>{{ project.orgMeta?.title || org.title }}</strong
                    >.
                </p>
                <p class="mb-4">Organizácia ťa bude kontaktovať na uvedený email alebo telefón.</p>
            </div>
        </template>
    </CModal>
    <ActionModal v-else show-image>
        <template #image><img alt="" src="@/assets/images/ico/feedbackThanks.svg" /></template>
        <template #title>
            <span>{{ $t('feedbacks.modal.title.thanksForFeedback', 'sk') }}</span>
        </template>
        <template #default>
            <p>Vďaka spätnej väzbe môžeme pre vás GROWNi zlepšovať.</p>
        </template>
        <template #footer>
            <div class="flex flex-col items-center">
                <CButton
                    class="w-1/2 uppercase"
                    :disabled="state === 'loading'"
                    :loading="state === 'loading'"
                    size="large"
                    variant="primary-150"
                    @click="close()"
                >
                    {{ $t('feedbacks.modal.buttons.ok', lang) }}
                </CButton>
            </div>
        </template>
    </ActionModal>
</template>

<script setup lang="ts">
import type { UnregisteredUser } from '#shared/types/JobRequest'
import type { LoadingState } from '#shared/types/LoadingState'
import type { Org } from '#shared/types/Org'
import type { VolunteeringWeekParticipationRequestFormData } from '#shared/types/ParticipationRequest'
import type { Project } from '#shared/types/Project'
import type { UserProfile } from '#shared/types/User'
import { getLength } from '#shared/utils'
import { getAvailableVolunteeringWeekSpots } from '#shared/utils/volunteeringWeekCapacity'
import { useHeadMeta } from '@/composables/head/useHeadMeta'
import { useProjects } from '@/composables/projects/useProjects'
import { useUser } from '@/composables/user/useUser'
import { useApplicationStore } from '@/stores/application'
import { normalizeSlovakPhoneNumber, validatePhoneNumber } from '@/validators/isPhoneNumber'
import { fetchSignInMethodsForEmail } from 'firebase/auth'
import type { ValidationErrors } from 'last-validator'
import { isEmail, isRequired, test, validate } from 'last-validator'
import cloneDeep from 'lodash-es/cloneDeep'

const props = defineProps<{
    project: Project
    org: Org
}>()

const emit = defineEmits<{
    (e: 'close', confirmed: boolean): void
}>()

const { t: $t, t } = useI18n()
const applicationStore = useApplicationStore()
const { createVolunteeringWeekParticipationRequest, doesPendingParticipationRequestExist } = useProjects()
const { updateUserProfile } = useUser()
const route = useRoute()

const userProfile = ref<UserProfile | null>(null)
const projectRequest = reactive<VolunteeringWeekParticipationRequestFormData>({
    email: '',
    backupContactMail: '',
    companyName: '',
    phone: '',
    motivation: '',
    volunteerCount: 1
})
const editableUserProfile = ref<UserProfile | undefined>(undefined)
const unregisteredUser = reactive<UnregisteredUser>({
    firstName: '',
    lastName: '',
    email: ''
})
const state = ref<LoadingState>('idle')
const errors = reactive<ValidationErrors>({})
const trackGtmEvent = ref(false)
const showFeedbackModalThanks = ref(false)
const consent = ref(false)
const { $auth } = useNuxtApp()

const availableSpots = computed(() => {
    return getAvailableVolunteeringWeekSpots(props.project) ?? 0
})
const hasAvailableSpots = computed(() => availableSpots.value > 0)

const showBackupContactMail = computed(() => Number(projectRequest.volunteerCount) > 1)
const showLoggedInNameFields = computed(() => {
    return Boolean(
        userProfile.value &&
            (!editableUserProfile.value?.firstName?.trim() || !editableUserProfile.value?.lastName?.trim())
    )
})

const lang = computed(() => {
    return 'sk'
})

const { headMetaTrackGTM } = useHeadMeta()
const validationErrorFields = [
    'email',
    'backupContactMail',
    'phone',
    'motivation',
    'volunteerCount',
    'firstName',
    'lastName',
    'consent'
] as const

useHead(() => {
    return trackGtmEvent.value
        ? headMetaTrackGTM(`event: 'user_request', request_type: 'project', modul_subtype: '${props.project.type}'`)
        : {}
})

async function fetchData() {
    state.value = 'loading'
    await reloadProfile()

    const userPrivateProfileData = applicationStore.userPrivateProfile
    const userProfileData = applicationStore.userProfile

    if (userProfileData) {
        if (userProfileData.firstName) {
            unregisteredUser.firstName = userProfileData.firstName
        }

        if (userProfileData.lastName) {
            unregisteredUser.lastName = userProfileData.lastName
        }
    }

    if (!projectRequest.email.trim() && userPrivateProfileData?.contactEmail) {
        projectRequest.email = userPrivateProfileData.contactEmail
    }

    if (!projectRequest.phone.trim() && userPrivateProfileData?.phone) {
        projectRequest.phone = userPrivateProfileData.phone
    }

    if (availableSpots.value < 1) {
        projectRequest.volunteerCount = 0
    } else {
        projectRequest.volunteerCount = 1
    }

    userProfile.value = applicationStore.userProfile ?? null
    editableUserProfile.value = userProfile.value ? cloneDeep(userProfile.value) : undefined

    state.value = 'idle'
}

async function submitApplication() {
    try {
        console.log('submitApplication called, userProfile:', userProfile.value ? 'exists' : 'not exists')
        state.value = 'loading'

        if (!hasAvailableSpots.value) {
            errors.general = 'Táto dobrovoľnícka príležitosť je už obsadená.'
            state.value = 'error'
            return
        }

        if (userProfile.value && userProfile.value.id) {
            console.log('Checking for pending request for logged in user')
            const hasPendingRequest = await doesPendingParticipationRequestExist(
                props.project.id!,
                userProfile.value.id
            )
            console.log('Has pending request:', hasPendingRequest)

            if (hasPendingRequest) {
                console.log('User already has a pending request')
                errors.general = 'Už máš odoslanú žiadosť na tento projekt. Počkaj na odpoveď organizácie.'
                state.value = 'error'
                return
            }
        }

        if (projectRequest.volunteerCount > availableSpots.value) {
            console.log(
                'Too many volunteers requested:',
                projectRequest.volunteerCount,
                'available:',
                availableSpots.value
            )
            errors.volunteerCount = `Počet ľudí nemôže byť väčší ako počet voľných miest (${availableSpots.value})`
            errors.general = 'Prosím, opravte chyby vo formulári'
            state.value = 'error'
            return
        }

        console.log('Validating project request')
        if (!(await validateProjectRequest())) {
            console.log('Project request validation failed')
            state.value = 'error'
            return
        }

        console.log('Checking if unregistered email is not used')
        await checkUnregisteredEmailNotUsed()

        await persistMissingProfileFields()

        console.log('Creating project request')
        await createProjectRequest()
        console.log('Project request created successfully')
        state.value = 'done'

        setTimeout(() => {
            console.log('Auto-closing modal after 3 seconds')
            close()
        }, 3000)

        trackGtmEvent.value = true
    } catch (e) {
        console.log('Error submitting project application:', e)
        state.value = 'error'
        errors.general = getErrorMessage(e)
        console.error('Error submitting project application:', e)
    }
}

async function createProjectRequest() {
    try {
        const requestData: VolunteeringWeekParticipationRequestFormData = {
            motivation: projectRequest.motivation,
            projectId: props.project.id,
            participantId: userProfile.value?.id,
            volunteerCount: projectRequest.volunteerCount,
            email: projectRequest.email,
            backupContactMail: showBackupContactMail.value
                ? projectRequest.backupContactMail?.trim() || undefined
                : undefined,
            companyName: projectRequest.companyName?.trim() || undefined,
            phone: normalizeSlovakPhoneNumber(projectRequest.phone),
            consent: consent.value,
            unregisteredUser: !userProfile.value
                ? {
                      firstName: unregisteredUser.firstName,
                      lastName: unregisteredUser.lastName,
                      email: projectRequest.email
                  }
                : undefined
        }

        console.log('Creating participation request with data:', requestData)
        await createVolunteeringWeekParticipationRequest(props.project.id!, requestData)
        await refreshProjectDetail()

        // Google analytics event
        if (process.client) {
            const ga = (window as any).$ga
            if (ga && ga.event) {
                ga.event({
                    eventCategory: 'project',
                    eventAction: 'submitApplicationModal'
                })
            }
        }
    } catch (error) {
        console.error('Error creating project request:', error)
        throw error
    }
}

function reloadProfile() {
    return applicationStore.reloadUserProfile()
}

async function refreshProjectDetail() {
    const projectDetailKey = `project-detail-${String(route.params.id || props.project.id)}`
    await refreshNuxtData(projectDetailKey)
}

function getErrorMessage(error: unknown) {
    if (typeof error === 'string') {
        return error
    }

    if (error instanceof Error) {
        const apiError = error as Error & {
            response?: {
                data?: {
                    error?: string
                }
            }
        }
        return apiError.response?.data?.error || error.message
    }

    return 'Nepodarilo sa odoslať žiadosť.'
}

async function isEmailInUse(email: string) {
    return (await fetchSignInMethodsForEmail($auth, email)).length
}

function onModalModelUpdate(isOpen: boolean) {
    if (!isOpen) {
        close()
    }
}

async function validateProjectRequest() {
    clearValidationErrors(...validationErrorFields)

    const isPhoneValid = test(
        (value: string) => validatePhoneNumber(value),
        'Neplatné telefónne číslo'
    )

    const isOptionalEmail = test(
        (value?: string) => !value?.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim()),
        'Neplatný email'
    )

    const isValidVolunteerCount = test(
        (value: number) => value > 0 && value <= availableSpots.value,
        `Počet ľudí nemôže byť väčší ako počet voľných miest (${availableSpots.value})`
    )

    const { errors: validationErrors, isValid } = await validate(
        {
            email: projectRequest.email,
            backupContactMail: projectRequest.backupContactMail,
            phone: projectRequest.phone,
            motivation: projectRequest.motivation,
            volunteerCount: projectRequest.volunteerCount,
            firstName: unregisteredUser.firstName,
            lastName: unregisteredUser.lastName,
            consent: consent.value
        },
        {
            email: [isRequired(), isEmail()],
            backupContactMail: [isOptionalEmail],
            phone: [isRequired(), isPhoneValid],
            motivation: [isRequired()],
            volunteerCount: [isRequired(), isValidVolunteerCount],
            firstName: !userProfile.value || showLoggedInNameFields.value ? [isRequired()] : [],
            lastName: !userProfile.value || showLoggedInNameFields.value ? [isRequired()] : [],
            consent: [isRequired('Súhlas je povinný')]
        }
    )

    Object.assign(errors, validationErrors)

    if (isValid) {
        projectRequest.phone = normalizeSlovakPhoneNumber(projectRequest.phone)
    }

    return isValid
}

function clearValidationErrors(...fields: ReadonlyArray<string>) {
    fields.forEach((field) => {
        delete errors[field]
    })
}

async function checkUnregisteredEmailNotUsed() {
    if (!userProfile.value && (await isEmailInUse(projectRequest.email))) {
        throw new Error('Tento email už je registrovaný na GROWNi. Môže ho používať iba prihlásený používateľ.')
    }
}

function close() {
    console.log('Close method called, state:', state.value)
    emit('close', state.value === 'done')
}

watch(showBackupContactMail, (isVisible) => {
    if (!isVisible) {
        projectRequest.backupContactMail = ''
    }
})

watch(
    () => applicationStore.userProfile,
    (profile) => {
        if (profile) {
            editableUserProfile.value = cloneDeep(profile)
        }
    },
    { deep: true }
)

watch(
    () => state.value,
    (value) => {
        if (value !== 'error') {
            delete errors.general
        }
    }
)

watch(
    () => showBackupContactMail.value,
    () => {
        delete errors.backupContactMail
    }
)

watch(
    () => projectRequest.phone,
    () => {
        delete errors.phone
    }
)

async function persistMissingProfileFields() {
    if (!userProfile.value?.id || !showLoggedInNameFields.value || !editableUserProfile.value) {
        return
    }

    const firstName = unregisteredUser.firstName?.trim()
    const lastName = unregisteredUser.lastName?.trim()

    if (!firstName || !lastName) {
        throw new Error('Doplň meno a priezvisko.')
    }

    const profileToPersist: UserProfile = {
        ...editableUserProfile.value,
        firstName,
        lastName
    }

    await updateUserProfile(userProfile.value.id, profileToPersist)
    await reloadProfile()
    userProfile.value = applicationStore.userProfile ?? null
    editableUserProfile.value = userProfile.value ? cloneDeep(userProfile.value) : undefined
}

onMounted(() => {
    fetchData()
})
</script>
